export const colors = {
  // Brand colors
  primary: '#f86400',
  primaryDark: '#d95700',
  primaryLight: '#fa8c40',

  // Full brand orange ramp (50-900) for tints/badges/hover states/gradients.
  // primary600 === primary, primary700 === primaryDark, kept as aliases so
  // existing references never break.
  primary50: '#fff7ed',
  primary100: '#ffedd5',
  primary200: '#fed7aa',
  primary300: '#fdba74',
  primary400: '#fb923c',
  primary500: '#fa8c40',
  primary600: '#f86400',
  primary700: '#d95700',
  primary800: '#b34700',
  primary900: '#7c3200',

  // Secondary
  secondary: '#A855F7',
  secondaryDark: '#9333EA',

  // Neutrals
  dark: '#1e293b',
  light: '#f8fafc',
  gray0: '#f7f9fbff',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray300: '#cbd5e1',
  gray400: '#94a3b8',
  gray500: '#64748b',
  gray600: '#475569',
  gray700: '#334155',
  gray800: '#1e293b',
  gray900: '#0f172a',

  // Status (React Toastify colors)
  success: '#07bc0c',
  successLight: '#dcfce7',
  successDark: '#166534',
  warning: '#f1c40f',
  warningLight: '#ffedd5',
  warningDark: '#9a3412',
  error: '#e74c3c',
  errorLight: '#fee2e2',
  errorDark: '#991b1b',
  danger: '#e74c3c',
  dangerDark: '#c0392b',
  info: '#3498db',
  infoLight: '#e0f2fe',
  infoDark: '#075985',

  // Background
  background: '#f8fafc',
  surface: '#ffffff',
  surfaceHover: '#fafafa',

  // Borders — a graduated scale instead of one flat gray so cards, inputs
  // and dividers can read as distinct without leaning on shadows alone.
  border: '#e2e8f0',
  borderSubtle: '#eef1f5',
  borderStrong: '#cbd5e1',

  // Text
  text: '#251e1e', // From SellerTheme MuiTypography
  textPrimary: '#251e1e',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  textDisabled: '#cbd5e1',
  textAccent: '#64748b',

  // Accent
  accent: '#f1f5f9',
};

export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  xxl: '3rem',
  xxxl: '4rem',
};

export const borderRadius = {
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  full: '9999px',
};

// Softer, lower-contrast shadows (Stripe/Veem-style) — the goal is depth you
// feel more than see. Pair with a hairline border where possible instead of
// reaching for a heavier shadow.
export const shadows = {
  sm: '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
  md: '0 2px 8px -2px rgba(15, 23, 42, 0.08)',
  lg: '0 8px 24px -4px rgba(15, 23, 42, 0.10)',
  xl: '0 20px 40px -8px rgba(15, 23, 42, 0.12)',
  card: '0 1px 3px 0 rgba(15, 23, 42, 0.06), 0 1px 2px -1px rgba(15, 23, 42, 0.04)',
  cardHover: '0 4px 16px -4px rgba(15, 23, 42, 0.10), 0 2px 6px -2px rgba(15, 23, 42, 0.06)',
  focusRing: (color: string) => `0 0 0 4px ${color}1f`,
};

export const typography = {
  fontFamily: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Fira Code', monospace",
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
};

export const breakpoints = {
  xs: '480px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  mobile: '640px',
  tablet: '768px',
  laptop: '1024px',
  desktop: '1280px',
  wide: '1536px',
};
